// [phase11-v1] The Signal — trading-ui client.
//
// Direct fetch over the same-origin /api/v1/matrix/* endpoints. The nginx vhost
// on port 8888 proxies /api/* to the backend on 127.0.0.1:8100, so the browser
// never sees the backend host directly.
//
// Backend endpoints:
//   GET /api/v1/signal/provider-health    (Phase 1)
//   GET /api/v1/signal/candidates         (Phase 8 + 9)
//
// Honest-empty contract: no fabricated probabilities, win rates, or P&L.
// All forbidden ML fields are intentionally absent from the type union.

export type SignalProviderStatus =
  | "LIVE"
  | "LIVE_PROXY"
  | "STALE"
  | "MISSING"
  | "UNAVAILABLE";

export type SignalFeatureStatus = "LIVE" | "LIMITED" | "BLOCKED";

export interface SignalProviderProbe {
  status: SignalProviderStatus;
  last_updated_utc: string | null;
  max_staleness_seconds: number;
  age_seconds: number | null;
  critical: boolean;
  reason: string | null;
}

export interface SignalProviderHealth {
  feature: string;
  status: SignalFeatureStatus;
  realtime_data_verified: boolean;
  generated_at_utc: string;
  generated_at_et: string;
  providers: Record<string, SignalProviderProbe>;
  blockers: Array<{
    provider: string;
    status: SignalProviderStatus;
    reason: string | null;
    age_seconds: number | null;
    max_staleness_seconds: number;
  }>;
  display_message: string;
  schema_version: number;
}

export type SignalCandidateStatus = "LIVE" | "EMPTY" | "GATED" | "ERROR";

export interface SignalScoreComponents {
  volume_quality: number;
  spread_quality: number;
  float_quality: number;
  price_quality: number;
  vix_regime: number;
  catalyst_recency: number;
}

export interface SignalCandidate {
  symbol: string;
  company_name?: string | null;
  price: number | null;
  spread_bps: number | null;
  premarket_volume: number;
  premarket_gap_pct?: number | null;
  float_value: number | null;
  float_class: string;
  catalyst_count?: number | null;
  sec_filing_flags?: Record<string, unknown>;
  halt_status_clear: boolean;
  vix_bucket: string;
  is_vix_proxy: boolean;
  passed_filters: string[];
  failed_filters: string[];
  data_sources_used: string[];
  accepted?: boolean;
  score: number;
  score_components: SignalScoreComponents;
  score_version: string;
  rank: number;
}

export interface SignalCandidatesPayload {
  feature: string;
  phase: number;
  status: SignalCandidateStatus;
  as_of_utc?: string;
  as_of_et?: string;
  realtime_data_verified_at_compute?: boolean;
  blockers_at_compute?: Array<{ provider: string; status: string }>;
  universe_size?: number;
  candidate_count?: number;
  candidates: SignalCandidate[];
  rejected_counts?: Record<string, number>;
  provider_snapshot?: Record<string, { status: string; age_seconds: number | null }>;
  reason?: string | null;
  schema_version: number;
  score_version?: string;
  cache_path?: string;
}

// ── Same-origin fetch (no auth, no headers) ─────────────────────────

export interface SignalFetchError {
  message: string;
  status?: number;
}

export type SignalResult<T> =
  | { ok: true; data: T }
  | { ok: false; error: SignalFetchError };

/**
 * The trading-ui backend wraps every response in a standard envelope:
 *   { ok: boolean, data: T | null, error: { code, message } | null, request_id, timestamp }
 *
 * Unwrap `.data` before returning so component code can treat it as `T` directly.
 * If the envelope reports `ok: false` (or `data` is missing) we surface the
 * envelope error verbatim so the UI can render a sensible message.
 */
interface BackendEnvelope<T> {
  ok: boolean;
  data: T | null;
  error: { code?: string; message?: string } | null;
  request_id?: string;
  timestamp?: string;
}

async function signalGet<T>(path: string): Promise<SignalResult<T>> {
  try {
    const res = await fetch(path, {
      method: "GET",
      headers: { Accept: "application/json" },
      cache: "no-store",
      credentials: "same-origin",
    });
    if (!res.ok) {
      return {
        ok: false,
        error: { message: `HTTP ${res.status}`, status: res.status },
      };
    }
    const body = (await res.json()) as BackendEnvelope<T> | T;
    // Detect envelope shape (presence of `ok`/`data` keys).
    if (
      body !== null &&
      typeof body === "object" &&
      "ok" in body &&
      "data" in body
    ) {
      const env = body as BackendEnvelope<T>;
      if (env.ok && env.data !== null && env.data !== undefined) {
        return { ok: true, data: env.data };
      }
      return {
        ok: false,
        error: {
          message: env.error?.message || "backend returned ok=false or data=null",
        },
      };
    }
    // Bare payload (no envelope) — treat as T.
    return { ok: true, data: body as T };
  } catch (e) {
    return { ok: false, error: { message: e instanceof Error ? e.message : String(e) } };
  }
}

export async function getSignalProviderHealth(): Promise<SignalResult<SignalProviderHealth>> {
  return signalGet<SignalProviderHealth>("/api/v1/signal/provider-health");
}

export async function getSignalCandidates(): Promise<SignalResult<SignalCandidatesPayload>> {
  return signalGet<SignalCandidatesPayload>("/api/v1/signal/candidates");
}

// ── Phase 11B — outcome labels ─────────────────────────────────────

/** All 9 closed Signal labels mirrored from schemas.matrix.labels. */
export type SignalLabel =
  | "tradable_runner"
  | "raw_runner_untradable"
  | "fade_after_open"
  | "opening_drive_then_dump"
  | "halt_to_zero"
  | "offering_kill"
  | "no_trade_liquidity"
  | "no_trade_spread"
  | "no_trade_data_unverified";

export interface SignalLabelRow {
  symbol: string;
  label: SignalLabel | string;
  label_reason: string;
  max_move_from_open_pct: number | null;
  sustained_above_3pct_minutes: number | null;
  simulated_net_pnl_pct: number | null;
}

export type SignalLabelsStatus =
  | "OK"
  | "PARTIAL"
  | "PENDING"
  | "ERROR"
  | "BARS_AUTH_MISSING"
  | "BARS_UNAVAILABLE"
  | "NO_CANDIDATES"
  | "WINDOW_NOT_CLOSED";

export interface SignalLabelsPayload {
  feature?: string;
  phase?: number;
  date: string;
  status: SignalLabelsStatus;
  reason?: string | null;
  label_counts: Record<string, number>;
  rows: SignalLabelRow[];
  row_count: number;
  schema_version: number;
  bars_status?: string;
  bars_errors?: string[];
  cache_path?: string;
  written_at_utc?: string;
}

export async function getSignalLabels(
  date?: string,
): Promise<SignalResult<SignalLabelsPayload>> {
  const qs = date ? `?date=${encodeURIComponent(date)}` : "";
  return signalGet<SignalLabelsPayload>(`/api/v1/signal/labels${qs}`);
}

/** Friendly label copy for the candidate-row outcome display. */
export function labelDisplay(label: string | null | undefined): {
  short: string;
  long: string;
  tone: "green" | "amber" | "red" | "blue" | "neutral";
} {
  switch (label) {
    case "tradable_runner":
      return { short: "Tradable runner", long: "Sustained ≥3% above open — would have been tradable.", tone: "green" };
    case "raw_runner_untradable":
      return { short: "Raw runner (untradable)", long: "Gapped 3% but did not sustain long enough to trade.", tone: "amber" };
    case "fade_after_open":
      return { short: "Fade after open", long: "Never gapped 3% from the open.", tone: "neutral" };
    case "opening_drive_then_dump":
      return { short: "Drive then dump", long: "Spiked at the open then closed below it.", tone: "red" };
    case "halt_to_zero":
      return { short: "Halt → zero", long: "Halted during the window and dropped below 50% of open.", tone: "red" };
    case "offering_kill":
      return { short: "Offering kill", long: "Offering-style SEC filing landed intraday.", tone: "red" };
    case "no_trade_liquidity":
      return { short: "No trade — liquidity", long: "Window total volume below the minimum-acceptance floor.", tone: "neutral" };
    case "no_trade_spread":
      return { short: "No trade — spread", long: "Premarket spread too wide to trade at the open.", tone: "neutral" };
    case "no_trade_data_unverified":
      return { short: "Data unverified", long: "Could not verify usable bars for the window.", tone: "neutral" };
    default:
      return { short: "Pending", long: "Label not yet computed.", tone: "blue" };
  }
}

// ── Phase 12 — backtest summary (historical paper replay only) ─────

export type SignalBacktestStatus =
  | "OK"
  | "PARTIAL"
  | "LABELS_MISSING"
  | "CANDIDATES_MISSING"
  | "EMPTY"
  | "ERROR"
  | "PENDING";

export interface SignalBacktestSummary {
  feature?: string;
  phase?: number;
  date: string;
  status: SignalBacktestStatus;
  reason?: string | null;
  candidate_count: number;
  labeled_count: number;
  tradable_runner_count: number;
  raw_runner_untradable_count: number;
  fade_after_open_count: number;
  opening_drive_then_dump_count: number;
  halt_to_zero_count: number;
  offering_kill_count: number;
  no_trade_liquidity_count: number;
  no_trade_spread_count: number;
  no_trade_data_unverified_count: number;
  hit_rate_all: number | null;
  hit_rate_top_1: number | null;
  hit_rate_top_3: number | null;
  hit_rate_top_5: number | null;
  average_simulated_net_pnl_pct_all: number | null;
  average_simulated_net_pnl_pct_top_1: number | null;
  average_simulated_net_pnl_pct_top_3: number | null;
  average_simulated_net_pnl_pct_top_5: number | null;
  median_simulated_net_pnl_pct: number | null;
  best_symbol: string | null;
  worst_symbol: string | null;
  score_vs_outcome_notes: string | null;
  schema_version: number;
  cache_path?: string;
  written_at_utc?: string;
}

export async function getSignalBacktest(
  date?: string,
): Promise<SignalResult<SignalBacktestSummary>> {
  const qs = date ? `?date=${encodeURIComponent(date)}` : "";
  return signalGet<SignalBacktestSummary>(`/api/v1/signal/backtest${qs}`);
}

// ── Display helpers ────────────────────────────────────────────────

export function providerMeta(key: string): { label: string; description: string } {
  switch (key) {
    case "premarket_bars":
      return { label: "Premarket Bars", description: "Alpaca IEX 1-min bars" };
    case "quotes_spread":
      return { label: "Quotes / Spread", description: "Alpaca latest-quote bid/ask" };
    case "news":
      return { label: "News", description: "Adversyn news cascade" };
    case "sec_filings":
      return { label: "SEC Filings", description: "EDGAR catalyst atom feed" };
    case "float":
      return { label: "Float Registry", description: "SEC XBRL companyfacts" };
    case "vix":
      return { label: "VIX (Proxy)", description: "VXX proxy via Alpaca" };
    case "halt_status":
      return { label: "Halt / LULD", description: "Alpaca trading-status WS" };
    default:
      return { label: key, description: "Signal provider" };
  }
}

export function formatAge(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return "—";
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
}

export function formatSla(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86400)}d`;
}

/**
 * Mon-Fri 04:00-10:30 America/New_York. Tracks the
 * adversyn-matrix-candidates.timer OnCalendar contract.
 */
export function isPremarketComputeWindow(now: Date = new Date()): boolean {
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    hour: "2-digit",
    minute: "2-digit",
    weekday: "short",
    hour12: false,
  });
  const parts = fmt.formatToParts(now);
  const weekday = parts.find((p) => p.type === "weekday")?.value || "";
  const hour = parseInt(parts.find((p) => p.type === "hour")?.value || "0", 10);
  const minute = parseInt(parts.find((p) => p.type === "minute")?.value || "0", 10);
  const dayOk = ["Mon", "Tue", "Wed", "Thu", "Fri"].includes(weekday);
  if (!dayOk) return false;
  const minuteOfDay = hour * 60 + minute;
  return minuteOfDay >= 240 && minuteOfDay < 630;
}
